import type { CanvasState, DrawingPath, TextBlock, CanvasImage } from "./types";

// Export at 2x resolution for sharp images and text
const DISPLAY_WIDTH = 375;
const DISPLAY_HEIGHT = DISPLAY_WIDTH / (9 / 16);
const EXPORT_SCALE = 2;
const CANVAS_WIDTH = DISPLAY_WIDTH * EXPORT_SCALE;
const CANVAS_HEIGHT = DISPLAY_HEIGHT * EXPORT_SCALE;

function parseColor(cssColor: string): string {
  if (cssColor.startsWith("#")) return cssColor;
  if (cssColor.startsWith("rgb")) {
    const m = cssColor.match(/\d+/g);
    if (m && m.length >= 3) {
      const r = parseInt(m[0], 10);
      const g = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    }
  }
  return "#ffffff";
}

export async function exportCanvasToBlob(state: CanvasState): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  ctx.fillStyle = parseColor(state.backgroundColor);
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  for (const path of state.drawings) {
    if (path.points.length < 2) continue;
    ctx.strokeStyle = path.color;
    ctx.lineWidth = path.strokeWidth * EXPORT_SCALE;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(path.points[0].x * EXPORT_SCALE, path.points[0].y * EXPORT_SCALE);
    for (let i = 1; i < path.points.length; i++) {
      ctx.lineTo(path.points[i].x * EXPORT_SCALE, path.points[i].y * EXPORT_SCALE);
    }
    ctx.stroke();
  }

  for (const img of state.images) {
    try {
      const image = await loadImage(img.src);
      ctx.save();
      // Scale to export resolution (canvas is 2x display size)
      const ex = img.x * EXPORT_SCALE;
      const ey = img.y * EXPORT_SCALE;
      const ew = img.width * EXPORT_SCALE;
      const eh = img.height * EXPORT_SCALE;
      ctx.translate(ex, ey);
      ctx.rotate((img.rotation * Math.PI) / 180);
      ctx.scale(img.scaleX, img.scaleY);
      ctx.drawImage(image, -ew / 2, -eh / 2, ew, eh);
      ctx.restore();
    } catch {
      // skip failed images
    }
  }

  ctx.textBaseline = "middle";
  ctx.direction = "rtl";
  for (const tb of state.textBlocks) {
    const align = tb.textAlign ?? "center";
    ctx.save();
    ctx.translate(tb.x * EXPORT_SCALE, tb.y * EXPORT_SCALE);
    ctx.rotate((tb.rotation * Math.PI) / 180);
    ctx.fillStyle = parseColor(tb.color);
    ctx.font = `${(tb.fontSize ?? 24) * EXPORT_SCALE}px ${tb.fontFamily}`;
    ctx.textAlign = align;
    const lines = (tb.text || "").split("\n");
    const lineHeight = (tb.fontSize ?? 24) * EXPORT_SCALE * 1.2;
    const startY = -((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, 0, startY + i * lineHeight);
    });
    ctx.restore();
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Export failed"))),
      "image/png",
      1
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}
